import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { orderBy, uniqBy } from 'lodash';
import classNames from 'classnames';
import qs from 'qs';

import { withStyles } from '@material-ui/core/styles';
import withTheme from '@material-ui/core/styles/withTheme';
import withWidth from '@material-ui/core/withWidth';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Typography from '@material-ui/core/Typography';

import { getFormValues, change, reset, submit } from 'redux-form';

import Progress from 'components/common/Progress';
import Layout from 'components/common/Layout';
import PatientDetailsBar from 'components/patient/PatientDetailsBar';
import OrdersFilter from 'components/workflow/OrdersFilter';
import OrdersTable from 'components/workflow/OrdersTable';
import PatientProfile from 'components/patient/PatientProfile';
import PatientsTabs from 'components/patient/PatientsTabs';
import OrdersActionsBar from 'components/workflow/OrdersActionsBar';
import PrintDialog from 'components/common/PrintDialog';
import DocumentsSignDialog from 'components/common/DocumentsSignDialog';
import Snackbar from 'components/common/Snackbar';
import Button from 'components/common/Button';
import IconButton from 'components/common/IconButton';
import IconFilter from 'components/icons/IconFilter';
import StickyToolbar from 'components/common/StickyToolbar';

import { fetchPatient } from 'redux/modules/patients';
import { fetchPatientGlobalNotes } from 'redux/modules/patientsGlobalNotes';
import { fetchOrders, clearOrders, updateOrderSuccess } from 'redux/modules/orders';
import { fetchArchiveOrders, clearArchiveOrders } from 'redux/modules/archiveOrders';
import { clearConnectedOrders } from 'redux/modules/connectedOrders';
import {
  addRouterParamsTab,
  addRouterParamsPageSettings,
} from 'redux/modules/routerParams';
import { addNotification } from 'redux/modules/notifications';
import { fetchBusiness } from 'redux/modules/business';
import { initializeUserNotifications } from 'redux/modules/userNotifications';

import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';
import formatPrice from 'utils/formatPrice';
import formatPrescriberName from 'utils/formatPrescriberName';
import formatSinglePlural from 'utils/formatSinglePlural';
import formatOrderNumber from 'utils/formatOrderNumber';
import * as validate from 'utils/validate';

import { appTitle, titleSeparator } from 'config/helmetSettings';
import orderLineTypes from 'config/orderLineTypes';
import dateFormat from 'config/dateFormat';
import timeDateFormat from 'config/timeDateFormat';
import timeFormat from 'config/timeFormat';

const styles = theme => ({
  patientProfileDetails: {
    marginTop: 145,
  },
  filter: {
    margin: 16,
    [theme.breakpoints.down('sm')]: {
      margin: '0 8px 8px 8px',
    },
  },
  ordersTableHead: {
    margin: '0 16px',
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      margin: '8px 0 0 8px',
    },
  },
  expansionPanelDetails: {
    padding: 0,
  },
  ordersContainer: {
    paddingTop: 261,
    [theme.breakpoints.down('sm')]: {
      paddingTop: 174,
    },
  },
  ordersTableHeadContainer: {
    [theme.breakpoints.down('sm')]: {
      overflowX: 'scroll',
    },
  },
  mobileFilterHeader: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      padding: 8,
      display: 'flex',
    },
  },
  buttonFilter: {
    display: 'block',
    marginLeft: -10,
    marginBottom: -14,
    marginTop: -16,
  },
  buttonFilterActive: {
    color: theme.palette.primary[500],
  },
  iconFilter: {
    width: 16,
    height: 16,
  },
});

class Patient extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: props.match.params.activeSection || 'profile',
      editPatientInformation: false,
      printDialogOpen: false,
      signDialogOpen: false,
      printFormValues: null,
      printDialogOrder: null,
      syncUpdatesSnackbarOpen: false,
      groupedUpdateSnackbarOpen: false,
      groupedUpdateValues: null,
      groupedUpdateOrderID: null,
      groupedUpdateEmptyDriverIDs: null,
      groupedUpdateEmptyDateOfDeliveryIDs: null,
      ordersActionsBarEntered: false,
      limit: 10,
      offset: 0,
      toolbarHeight: 0,
      toolbarExpanded: false,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      mobileFilterHeight: 0,
    };

    this.onOrdersRequest = this.onOrdersRequest.bind(this);
    this.onTabChange = this.onTabChange.bind(this);
    this.onSelectedOrderLinesPrint = this.onSelectedOrderLinesPrint.bind(this);
    this.onPrintDialogClose = this.onPrintDialogClose.bind(this);
    this.onPrintDialogSign = this.onPrintDialogSign.bind(this);
    this.onSignDialogClose = this.onSignDialogClose.bind(this);
    this.onSortingChange = this.onSortingChange.bind(this);
    this.onFormChange = this.onFormChange.bind(this);
    this.onFormSubmitSuccess = this.onFormSubmitSuccess.bind(this);
    this.onFormTextFieldBlur = this.onFormTextFieldBlur.bind(this);
    this.onGroupedUpdateConfirm = this.onGroupedUpdateConfirm.bind(this);
    this.onGroupedUpdateSnackbarClose = this.onGroupedUpdateSnackbarClose.bind(this);
    this.onSyncUpdatesConfirm = this.onSyncUpdatesConfirm.bind(this);
    this.onSyncUpdatesSnackbarClose = this.onSyncUpdatesSnackbarClose.bind(this);
    this.onOrdersActionsBarEntered = this.onOrdersActionsBarEntered.bind(this);
    this.onOrdersActionsBarExited = this.onOrdersActionsBarExited.bind(this);
    this.onWindowKeyDowm = this.onWindowKeyDowm.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);
    this.onNotesRequest = this.onNotesRequest.bind(this);
    this.onEditPatientInformationButtonClick = this.onEditPatientInformationButtonClick.bind(this);
    this.onEditPatientInformationCancel = this.onEditPatientInformationCancel.bind(this);
    this.onWindowMessage = this.onWindowMessage.bind(this);
    this.onToolbarResize = this.onToolbarResize.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);

    this.onThrottledOrdersRequest = throttle(this.onOrdersRequest, 2000, false, true);
  }

  componentWillMount() {
    const { actions } = this.props;
    actions.clearOrders();
    actions.clearConnectedOrders();
  }

  componentDidMount() {
    const { actions, match, userNotificationsInitialized } = this.props;
    const { activeSection } = this.state;
    actions.fetchPatient({
      'Record Number': match.params.id,
      triggerLoading: true,
    });
    actions.fetchPatientGlobalNotes({
      values: {
        'Record Number': [match.params.id],
      },
      triggerLoading: true,
    });
    actions.fetchBusiness({
      triggerLoading: false,
    });
    if (['summary', 'history'].indexOf(activeSection) !== -1) {
      this.onOrdersRequest();
    }
    if (!userNotificationsInitialized) {
      actions.initializeUserNotifications();
    }
    window.addEventListener('message', this.onWindowMessage);
  }

  componentWillReceiveProps(nextProps) {
    const {
      loading,
      ordersFilters,
      connectedOrdersOrderLineID,
      actions,
      location,
      history,
      patient,
      sortBy,
      sort,
      tabs,
      archive,
    } = this.props;
    const { activeSection } = this.state;
    const nextOrderState = nextProps.ordersFilters['Workflow State']
      ? nextProps.ordersFilters['Workflow State'].join('')
      : null;
    const currentOrderState = ordersFilters['Workflow State']
      ? ordersFilters['Workflow State'].join('')
      : null;
    const nextIsRejected = nextProps.ordersFilters['Is Rejected']
      ? nextProps.ordersFilters['Is Rejected'].join('')
      : null;
    const currentIsRejected = ordersFilters['Is Rejected']
      ? ordersFilters['Is Rejected'].join('')
      : null;
    const nextAssignedUserID = nextProps.ordersFilters['Assigned User ID']
      ? nextProps.ordersFilters['Assigned User ID'].join('')
      : null;
    const currentAssignedUserID = ordersFilters['Assigned User ID']
      ? ordersFilters['Assigned User ID'].join('')
      : null;
    const nextStatus = nextProps.ordersFilters.Status
      ? nextProps.ordersFilters.Status.join('')
      : null;
    const currentStatus = ordersFilters.Status
      ? ordersFilters.Status.join('')
      : null;
    const nextDriverID = nextProps.ordersFilters['Driver ID']
      ? nextProps.ordersFilters['Driver ID'].join('')
      : null;
    const currentDriverID = ordersFilters['Driver ID']
      ? ordersFilters['Driver ID'].join('')
      : null;
    const nextInsuranceInfoID = nextProps.ordersFilters['Insurance Info ID']
      ? nextProps.ordersFilters['Insurance Info ID'].join('')
      : null;
    const currentInsuranceInfoID = ordersFilters['Insurance Info ID']
      ? ordersFilters['Insurance Info ID'].join('')
      : null;
    const nextLineType = nextProps.ordersFilters['Line Type']
      ? nextProps.ordersFilters['Line Type'].join('')
      : null;
    const currentLineType = ordersFilters['Line Type']
      ? ordersFilters['Line Type'].join('')
      : null;
    const nextSalesID = nextProps.ordersFilters['Sales Representative ID']
      ? nextProps.ordersFilters['Sales Representative ID'].join('')
      : null;
    const currentSalesID = ordersFilters['Sales Representative ID']
      ? ordersFilters['Sales Representative ID'].join('')
      : null;
    const nextReferralID = nextProps.ordersFilters['Referral Source ID']
      ? nextProps.ordersFilters['Referral Source ID'].join('')
      : null;
    const currentReferralID = ordersFilters['Referral Source ID']
      ? ordersFilters['Referral Source ID'].join('')
      : null;
    const nextPrescriberID = nextProps.ordersFilters['Prescriber ID']
    ? nextProps.ordersFilters['Prescriber ID'].join('')
    : null;
    const currentPrescriberID = ordersFilters['Prescriber ID']
      ? ordersFilters['Prescriber ID'].join('')
      : null;
    const filtersChanged =
      nextProps.ordersFilters['Order ID'] !== ordersFilters['Order ID']
      || nextProps.ordersFilters['Claim Code'] !== ordersFilters['Claim Code']
      || nextProps.ordersFilters['Date of Service From'] !== ordersFilters['Date of Service From']
      || nextProps.ordersFilters['Date of Service To'] !== ordersFilters['Date of Service To']
      || nextProps.ordersFilters['Date of Creation From'] !== ordersFilters['Date of Creation From']
      || nextProps.ordersFilters['Date of Creation To'] !== ordersFilters['Date of Creation To']
      || nextProps.ordersFilters['Date of Delivery From'] !== ordersFilters['Date of Delivery From']
      || nextProps.ordersFilters['Date of Delivery To'] !== ordersFilters['Date of Delivery To']
      || nextProps.ordersFilters['Date of Claim Submission From'] !== ordersFilters['Date of Claim Submission From']
      || nextProps.ordersFilters['Date of Claim Submission To'] !== ordersFilters['Date of Claim Submission To']
      || nextOrderState !== currentOrderState
      || nextIsRejected !== currentIsRejected
      || nextAssignedUserID !== currentAssignedUserID
      || nextStatus !== currentStatus
      || nextDriverID !== currentDriverID
      || nextInsuranceInfoID !== currentInsuranceInfoID
      || nextLineType !== currentLineType
      || nextSalesID !== currentSalesID
      || nextReferralID !== currentReferralID
      || nextPrescriberID !== currentPrescriberID;
    const sortByChanged = nextProps.sortBy !== sortBy;
    const sortChanged = nextProps.sort !== sort;
    const archiveChanged = nextProps.archive !== archive;
    if ((loading > 0
      && nextProps.loading === 0
      && (!nextProps.patient || !nextProps.patient['First Name']))
      || (tabs.length > 0 && nextProps.tabs.length === 0)
      || (nextProps.tabs.findIndex(item => item.path === location.pathname) === -1
        && tabs.findIndex(item => item.path === location.pathname) !== -1)) {
      history.replace('/charts');
    }
    if (nextProps.patient
      && nextProps.patient['First Name'] && nextProps.patient['Last Name']
      && ((nextProps.tabs.findIndex(item => item.path === location.pathname) === -1
        && tabs.findIndex(item => item.path === location.pathname) === -1)
        || (patient && patient['First Name'] !== nextProps.patient['First Name'])
        || (patient && patient['Last Name'] !== nextProps.patient['Last Name']))
      && !(tabs.length > 0 && nextProps.tabs.length === 0)) {
      actions.addRouterParamsTab({
        patient: {
          ID: nextProps.patient['Record Number'],
          path: location.pathname,
          title: `${nextProps.patient['Last Name']}, ${nextProps.patient['First Name']}`,
        },
      });
    }
    if (activeSection !== 'profile') {
      if (filtersChanged || sortChanged || sortByChanged || archiveChanged) {
        this.setState({ offset: 0 });
        window.scrollTo(0, 0);
      }

      if (nextProps.connectedOrdersOrderLineID.length > 0
        && JSON.stringify(nextProps.connectedOrdersOrderLineID)
        !== JSON.stringify(connectedOrdersOrderLineID)) {
        actions.clearConnectedOrders();
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { ordersFilters, notesFilters, sortBy, sort, orders, archive } = this.props;
    const { activeSection, limit, offset } = this.state;

    if (activeSection === 'summary' || activeSection === 'history') {
      const limitChanged = prevState.limit !== limit;
      const offsetChanged = prevState.offset !== offset;
      const prevOrderState = prevProps.ordersFilters['Workflow State']
      ? prevProps.ordersFilters['Workflow State'].join('')
      : null;
      const currentOrderState = ordersFilters['Workflow State']
        ? ordersFilters['Workflow State'].join('')
        : null;
      const prevAssignedUserID = prevProps.ordersFilters['Assigned User ID']
        ? prevProps.ordersFilters['Assigned User ID'].join('')
        : null;
      const currentAssignedUserID = ordersFilters['Assigned User ID']
        ? ordersFilters['Assigned User ID'].join('')
        : null;
      const prevStatus = prevProps.ordersFilters.Status
        ? prevProps.ordersFilters.Status.join('')
        : null;
      const currentStatus = ordersFilters.Status
        ? ordersFilters.Status.join('')
        : null;
      const prevIsRejected = prevProps.ordersFilters['Is Rejected']
        ? prevProps.ordersFilters['Is Rejected'].join('')
        : null;
      const currentIsRejected = ordersFilters['Is Rejected']
        ? ordersFilters['Is Rejected'].join('')
        : null;
      const prevDriverID = prevProps.ordersFilters['Driver ID']
        ? prevProps.ordersFilters['Driver ID'].join('')
        : null;
      const currentDriverID = ordersFilters['Driver ID']
        ? ordersFilters['Driver ID'].join('')
        : null;
      const prevInsuranceInfoID = prevProps.ordersFilters['Insurance Info ID']
        ? prevProps.ordersFilters['Insurance Info ID'].join('')
        : null;
      const currentInsuranceInfoID = ordersFilters['Insurance Info ID']
        ? ordersFilters['Insurance Info ID'].join('')
        : null;
      const prevLineType = prevProps.ordersFilters['Line Type']
        ? prevProps.ordersFilters['Line Type'].join('')
        : null;
      const currentLineType = ordersFilters['Line Type']
        ? ordersFilters['Line Type'].join('')
        : null;
      const prevSalesID = prevProps.ordersFilters['Sales Representative ID']
        ? prevProps.ordersFilters['Sales Representative ID'].join('')
        : null;
      const currentSalesID = ordersFilters['Sales Representative ID']
        ? ordersFilters['Sales Representative ID'].join('')
        : null;
      const prevReferralID = prevProps.ordersFilters['Referral Source ID']
        ? prevProps.ordersFilters['Referral Source ID'].join('')
        : null;
      const currentReferralID = ordersFilters['Referral Source ID']
        ? ordersFilters['Referral Source ID'].join('')
        : null;
      const prevPrescriberID = prevProps.ordersFilters['Prescriber ID']
        ? prevProps.ordersFilters['Prescriber ID'].join('')
        : null;
      const currentPrescriberID = ordersFilters['Prescriber ID']
        ? ordersFilters['Prescriber ID'].join('')
        : null;
      const throttledFiltersChanged =
        (ordersFilters['Order ID']
        && ordersFilters['Order ID'].length > 0
        && prevProps.ordersFilters['Order ID'] !== ordersFilters['Order ID'])
        || (ordersFilters['Claim Code']
        && ordersFilters['Claim Code'].length > 0
        && prevProps.ordersFilters['Claim Code'] !== ordersFilters['Claim Code']);
      const filtersChanged = (!ordersFilters['Order ID']
        && prevProps.ordersFilters['Order ID']
        && prevProps.ordersFilters['Order ID'].length > 0
        && prevProps.ordersFilters['Order ID'] !== ordersFilters['Order ID'])
        || (!ordersFilters['Claim Code']
        && prevProps.ordersFilters['Claim Code']
        && prevProps.ordersFilters['Claim Code'].length > 0
        && prevProps.ordersFilters['Claim Code'] !== ordersFilters['Claim Code'])
        || prevProps.ordersFilters['Date of Service From'] !== ordersFilters['Date of Service From']
        || prevProps.ordersFilters['Date of Service To'] !== ordersFilters['Date of Service To']
        || prevProps.ordersFilters['Date of Creation'] !== ordersFilters['Date of Creation']
        || prevProps.ordersFilters['Date of Creation From'] !== ordersFilters['Date of Creation From']
        || prevProps.ordersFilters['Date of Creation To'] !== ordersFilters['Date of Creation To']
        || prevProps.ordersFilters['Date of Delivery From'] !== ordersFilters['Date of Delivery From']
        || prevProps.ordersFilters['Date of Delivery To'] !== ordersFilters['Date of Delivery To']
        || prevProps.ordersFilters['Date of Claim Submission From'] !== ordersFilters['Date of Claim Submission From']
        || prevProps.ordersFilters['Date of Claim Submission To'] !== ordersFilters['Date of Claim Submission To']
        || prevOrderState !== currentOrderState
        || prevAssignedUserID !== currentAssignedUserID
        || prevStatus !== currentStatus
        || prevIsRejected !== currentIsRejected
        || prevDriverID !== currentDriverID
        || prevInsuranceInfoID !== currentInsuranceInfoID
        || prevLineType !== currentLineType
        || prevSalesID !== currentSalesID
        || prevReferralID !== currentReferralID
        || prevPrescriberID !== currentPrescriberID;
      const sortByChanged = prevProps.sortBy !== sortBy;
      const sortChanged = prevProps.sort !== sort;
      const archiveChanged = prevProps.archive !== archive;
      if (throttledFiltersChanged) {
        this.onThrottledOrdersRequest();
      } else if (limitChanged
        || offsetChanged
        || filtersChanged
        || sortByChanged
        || sortChanged
        || archiveChanged) {
        this.onOrdersRequest();
      }

      if (orders.length !== prevProps.orders.length
        && this.scrollRef
        && this.scrollRef.scrollTop === 0) {
        this.onTableScroll({ target: this.scrollRef });
      }
    } else {
      const prevNotesCategoryID = prevProps.notesFilters['Notes Category ID']
        ? prevProps.notesFilters['Notes Category ID'].join('')
        : null;
      const currentNotesCategoryID = notesFilters['Notes Category ID']
        ? notesFilters['Notes Category ID'].join('')
        : null;
      const prevNotesAuthorID = prevProps.notesFilters['Author ID']
        ? prevProps.notesFilters['Author ID'].join('')
        : null;
      const currentNotesAuthorID = notesFilters['Author ID']
        ? notesFilters['Author ID'].join('')
        : null;
      const notesFiltersChanged = prevNotesCategoryID !== currentNotesCategoryID
        || prevNotesAuthorID !== currentNotesAuthorID
        || prevProps.notesFilters['Date of Creation From'] !== notesFilters['Date of Creation From']
        || prevProps.notesFilters['Date of Creation To'] !== notesFilters['Date of Creation To'];
      if (notesFiltersChanged) {
        this.onNotesRequest();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onWindowMessage);
  }

  onOrdersRequest(options = {}) {
    const {
      actions,
      ordersFilters,
      sortBy,
      sort,
      archive,
    } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = Object.keys(ordersFilters).map((key) => {
      let name = key;
      let operation;
      let caseSensitivity = null;
      let val = ordersFilters[key];
      switch (key) {
        case 'Workflow State':
            name = 'Workflow State ID';
          if (ordersFilters[key].length > 1) {
            operation = 'in';
          } else {
            operation = 'eq';
            val = ordersFilters[key][0];
          }
          break;
        case 'Is Rejected':
          if (ordersFilters[key].length > 1) {
            operation = 'in';
          } else {
            operation = 'eq';
            val = ordersFilters[key][0];
          }
          break;
        case 'Order ID':
          name = 'ID';
          operation = 'eq';
          break;
        case 'Claim Code':
          operation = 'startswith';
          caseSensitivity = false;
          break;
        case 'Assigned User ID':
        case 'Driver ID':
        case 'Insurance Info ID':
        case 'Line Type':
        case 'Sales Representative ID':
        case 'Referral Source ID':
        case 'Prescriber ID':
          operation = 'in';
          break;
        case 'Date of Service From':
          name = 'Date of Service';
          operation = 'ge';
          break;
        case 'Date of Service To':
          name = 'Date of Service';
          operation = 'le';
          break;
        case 'Date of Creation':
          name = 'Date of Creation';
          operation = 'eq';
          break;
        case 'Date of Creation From':
          name = 'Date of Creation';
          operation = 'ge';
          break;
        case 'Date of Creation To':
          name = 'Date of Creation';
          operation = 'le';
          break;
        case 'Date of Delivery From':
          name = 'Date of Delivery';
          operation = 'ge';
          break;
        case 'Date of Delivery To':
          name = 'Date of Delivery';
          operation = 'le';
          break;
        case 'Date of Claim Submission From':
          name = 'Claim Create At';
          operation = 'ge';
          break;
        case 'Date of Claim Submission To':
          name = 'Claim Create At';
          operation = 'le';
          break;
        case 'Status':
          name = 'Workflow Status ID';
          operation = 'in';
          break;
        case 'Patient ID':
          operation = 'eq';
          break;
        default:
          operation = 'startswith';
          caseSensitivity = false;
      }
      return {
        name,
        operation,
        val,
        ...caseSensitivity !== null ? { case_sensitivity: caseSensitivity } : {},
      };
    });
    actions.fetchOrders({
      limit: options.limit !== undefined ? options.limit : limit,
      offset: options.offset !== undefined ? options.offset : offset,
      filters: requestFilters,
      ...sortBy ? {
        sortBy: [{
          name: sortBy,
          sort,
        }],
      } : {},
      triggerLoading: true,
      push: options.push !== undefined ? options.push : offset > 0,
    });
    if (archive) {
      actions.fetchArchiveOrders({
        limit: options.limit !== undefined ? options.limit : limit,
        offset: options.offset !== undefined ? options.offset : offset,
        filters: requestFilters,
        ...sortBy ? {
          sortBy: [{
            name: sortBy,
            sort,
          }],
        } : {},
        triggerLoading: true,
        push: options.push !== undefined ? options.push : offset > 0,
      });
    }
  }

  onNotesRequest() {
    const {
      actions,
      notesFilters,
      patient,
    } = this.props;
    const requestFilters = Object.keys(notesFilters).map((key) => {
      let name = key;
      let operation;
      let caseSensitivity = null;
      const val = notesFilters[key];
      switch (key) {
        case 'Notes Category ID':
        case 'Author ID':
          operation = 'in';
          break;
        case 'Date of Creation From':
          name = 'Date of Creation';
          operation = 'ge';
          break;
        case 'Date of Creation To':
          name = 'Date of Creation';
          operation = 'le';
          break;
        default:
          operation = 'startswith';
          caseSensitivity = false;
      }
      return {
        name,
        operation,
        val,
        ...caseSensitivity !== null ? { case_sensitivity: caseSensitivity } : {},
      };
    });
    actions.fetchPatientGlobalNotes({
      values: {
        'Record Number': [patient['Record Number']],
        filters: requestFilters,
      },
      triggerLoading: true,
    });
  }

  onWindowKeyDowm(event) {
    if (event.keyCode === 13) {
      if (this.state.syncUpdatesSnackbarOpen) {
        this.onSyncUpdatesConfirm();
      } else {
        this.onGroupedUpdateConfirm();
      }
    }
  }

  onTabChange(event, value) {
    const { history, patient } = this.props;
    history.replace(`/charts/${patient['Record Number']}/${value}`);
    this.setState({
      activeSection: value,
      triggerLoading: true,
    });
  }

  onSelectedOrderLinesPrint(order) {
    this.setState({
      printDialogOpen: true,
      printDialogOrder: order,
    });
  }

  onPrintDialogClose() {
    this.setState({ printDialogOpen: false });
  }

  onPrintDialogSign(values) {
    this.setState({
      printDialogOpen: false,
      printFormValues: values,
      signDialogOpen: true,
    });
  }

  onSignDialogClose() {
    this.setState({ signDialogOpen: false });
  }

  onSortingChange(newSortBy) {
    const { actions, location, sort, sortBy } = this.props;
    let newSort;
    if (sortBy !== newSortBy) {
      newSort = newSortBy === 'Date of Creation' ? 'desc' : 'asc';
    } else {
      newSort = sort === 'asc' ? 'desc' : 'asc';
    }
    actions.addRouterParamsPageSettings({
      [location.pathname]: {
        sortBy: newSortBy,
        sort: newSort,
      },
    });
  }

  onFormSubmitSuccess(values) {
    const { orderLines, actions } = this.props;
    const { groupedUpdateEmptyDriverIDs, groupedUpdateEmptyDateOfDeliveryIDs, limit, offset } = this.state;
    let emptyDriverIDs = values
      .filter(orderLine => (orderLine['Workflow Status'] && orderLine['Workflow Status'].filter(statusItem =>
        statusItem['Workflow State'].ID === 2).length > 0
        && !orderLine.Driver.ID))
      .map(orderLine => orderLine.ID);
    let emptyDateOfDeliveryIDs = values
      .filter(orderLine => (orderLine['Workflow Status'] && orderLine['Workflow Status'].filter(statusItem =>
        statusItem['Workflow State'].ID === 2).length > 0
        && !orderLine['Date of Delivery']))
      .map(orderLine => orderLine.ID);
    if (groupedUpdateEmptyDriverIDs) {
      emptyDriverIDs = [
        ...emptyDriverIDs,
        ...groupedUpdateEmptyDriverIDs,
      ];
    }
    if (groupedUpdateEmptyDateOfDeliveryIDs) {
      emptyDateOfDeliveryIDs = [
        ...emptyDateOfDeliveryIDs,
        ...groupedUpdateEmptyDateOfDeliveryIDs,
      ];
    }
    if (this.state.groupedUpdateValues) {
      this.setState({
        groupedUpdateSnackbarOpen: true,
        groupedUpdateEmptyDriverIDs: emptyDriverIDs.length > 0 ? emptyDriverIDs : null,
        groupedUpdateEmptyDateOfDeliveryIDs: emptyDateOfDeliveryIDs.length > 0 ? emptyDateOfDeliveryIDs : null,
      });
      window.addEventListener('keydown', this.onWindowKeyDowm);
    } else if (emptyDriverIDs.length > 0 || emptyDateOfDeliveryIDs.length > 0) {
      const notificationMessage = [
        emptyDriverIDs.length > 0
          ? `${formatSinglePlural({
            quantity: emptyDriverIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${orderLines
              .filter(item => emptyDriverIDs.indexOf(item.ID) !== -1)
              .map(item => item['HCPC S Code']['HCPC S Code'])
              .join(', ')} ${formatSinglePlural({
            quantity: emptyDriverIDs.length,
            single: 'has',
            plural: 'have',
          })} missing Driver information`
          : null,
        emptyDateOfDeliveryIDs.length > 0
          ? `${formatSinglePlural({
            quantity: emptyDateOfDeliveryIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${orderLines
              .filter(item => emptyDateOfDeliveryIDs.indexOf(item.ID) !== -1)
              .map(item => item['HCPC S Code']['HCPC S Code'])
              .join(', ')} ${formatSinglePlural({
            quantity: emptyDateOfDeliveryIDs.length,
            single: 'has',
            plural: 'have',
          })} missing Date of Delivery information`
          : null,
      ].filter(item => item).join(', ');
      actions.addNotification({
        message: notificationMessage,
        variant: 'info',
      });
      this.setState({
        groupedUpdateEmptyDriverIDs: null,
        groupedUpdateEmptyDateOfDeliveryIDs: null,
      });
    }
    this.onOrdersRequest({
      push: false,
      offset: 0,
      limit: offset + limit,
    });
  }

  onFormChange(values, dispatch, props, prevValues) {
    const { patient, actions, orders, selectedOrderLines } = this.props;
    const diffValues = objectDiff(values, prevValues);
    const changeFieldName = Object.keys(diffValues)[0];
    const statusChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName].Status !== undefined;
    const assignedUserChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Assigned User ID'];
    const driverChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Driver ID'] !== undefined;
    const placeOfServiceChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Place of SRV'] !== undefined;
    const dateOfDeliveryChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Date of Delivery'] !== undefined;
    const timeOfDeliveryChanged = diffValues[changeFieldName]
      && diffValues[changeFieldName]['Time of Delivery'] !== undefined;
    if (changeFieldName
      && changeFieldName.indexOf('-orderLine') === -1
      && Object.keys(diffValues).length === 1) {
      const changeOrderID = Number(changeFieldName.replace('-order-selected', ''));
      const changeOrder = orders.find(item => item.ID === changeOrderID);
      if (diffValues[changeFieldName] && !prevValues[changeFieldName]) {
        changeOrder['Order Line'].forEach((orderLine) => {
          actions.change(`${patient['Record Number']}-ordersForm`, `${orderLine.ID}-orderLine.selected`, true);
        });
      } else if (prevValues[changeFieldName]
          && !diffValues[changeFieldName]
          && changeOrder['Order Line'].filter(orderLine => values[`${orderLine.ID}-orderLine`].selected).length
        === changeOrder['Order Line'].length) {
        changeOrder['Order Line'].forEach((orderLine) => {
          actions.change(`${patient['Record Number']}-ordersForm`, `${orderLine.ID}-orderLine.selected`, false);
        });
        actions.change(`${patient['Record Number']}-ordersForm`, changeFieldName, false);
      }
    } else if (diffValues[changeFieldName]
      && diffValues[changeFieldName].selected !== undefined) {
      const changeOrderLineID = Number(changeFieldName.replace('-orderLine', ''));
      const changeOrder = orders.find(order => order['Order Line'].find(orderLine =>
        orderLine.ID === changeOrderLineID));
      if (diffValues[changeFieldName].selected === false
        && values[`${changeOrder.ID}-order-selected`] === true) {
        actions.change(`${patient['Record Number']}-ordersForm`, `${changeOrder.ID}-order-selected`, false);
      } else if (diffValues[changeFieldName].selected === true
        && values[`${changeOrder.ID}-order-selected`] === false) {
        const allOrderLinesSelected = changeOrder['Order Line'].filter(orderLine =>
          values[`${orderLine.ID}-orderLine`].selected === true).length
          === changeOrder['Order Line'].length;
        if (allOrderLinesSelected) {
          actions.change(`${patient['Record Number']}-ordersForm`, `${changeOrder.ID}-order-selected`, true);
        }
      }
    } else {
      Object.keys(values).forEach((orderLine) => {
        if (values[orderLine]['Assigned User ID']
          && values[orderLine]['Selected Assigned User Name']
            !== values[orderLine]['Assigned User Name']) {
          dispatch(props.change(`${orderLine}.Assigned User ID`, ''));
        }

        if (values[orderLine]['Driver ID']
          && values[orderLine]['Selected Driver Name']
            !== values[orderLine]['Driver Name']) {
          dispatch(props.change(`${orderLine}.Driver ID`, ''));
        }
      });

      if (!this.groupedUpdate
        && !props.pristine
        && diffValues[changeFieldName].selected === undefined
        && (diffValues[changeFieldName].Reimbs === undefined)
        && diffValues[changeFieldName]['Reimbs Date'] === undefined
        && diffValues[changeFieldName]['Check Book Sheet'] === undefined
        && diffValues[changeFieldName].orderLineInsuranceParam === undefined
      ) {
        if (selectedOrderLines.length > 0) {
          if (selectedOrderLines.filter(item => `${item.ID}-orderLine` === changeFieldName).length > 0) {
            const selectedOrderLinesOrderIDs = [];
            this.groupedUpdate = true;
            selectedOrderLines.forEach((orderLine) => {
              if (selectedOrderLinesOrderIDs.indexOf(orderLine['Order ID']) === -1) {
                selectedOrderLinesOrderIDs.push(orderLine['Order ID']);
              }
              Object.keys(diffValues[changeFieldName]).forEach((filedName) => {
                const changeValue = filedName === 'Status' ? values[changeFieldName][filedName] : diffValues[changeFieldName][filedName];
                actions.change(`${patient['Record Number']}-ordersForm`, `${orderLine.ID}-orderLine.${filedName}`, changeValue);
              });
              if (diffValues[changeFieldName]['Assigned User Name']
                && !diffValues[changeFieldName]['Selected Assigned User Name']) {
                actions.change(`${patient['Record Number']}-ordersForm`,
                  `${orderLine.ID}-orderLine.Selected Assigned User Name`,
                  diffValues[changeFieldName]['Assigned User Name']);
              }
            });
            // Delay to wait multiple async change actions to complete
            setTimeout(() => {
              if (selectedOrderLinesOrderIDs.length > 1) {
                this.setState({ syncUpdatesSnackbarOpen: true });
                window.addEventListener('keydown', this.onWindowKeyDowm);
              } else {
                actions.submit(`${patient['Record Number']}-ordersForm`);
              }
              this.groupedUpdate = false;
            }, 200);
          } else {
            setTimeout(() => {
              actions.submit(`${patient['Record Number']}-ordersForm`);
            }, 100);
          }
        } else if (statusChanged || assignedUserChanged || driverChanged
          || placeOfServiceChanged || dateOfDeliveryChanged || timeOfDeliveryChanged) {
          const changeOrderLineID = Number(changeFieldName.replace('-orderLine', ''));
          const changeOrder = orders.find(order =>
            order['Order Line'].findIndex(orderLine =>
              orderLine.ID === changeOrderLineID) !== -1);
          if (changeOrder['Order Line'].length > 1) {
            this.setState({
              groupedUpdateValues: {
                ...diffValues[changeFieldName],
                ...diffValues[changeFieldName]['Driver ID'] === '' ? {
                  'Driver Name': '',
                  'Selected Driver Name': '',
                } : {},
              },
              groupedUpdateOrderID: changeOrder.ID,
            });
          }
          setTimeout(() => {
            actions.submit(`${patient['Record Number']}-ordersForm`);
          }, 100);
        }
      }
    }
  }

  onFormTextFieldBlur(diffValues) {
    const { patient, orders, actions, selectedOrderLines } = this.props;
    const changeFieldName = Object.keys(diffValues)[0];
    const changeOrderLineID = Number(changeFieldName.replace('-orderLine', ''));
    if (selectedOrderLines.length > 0) {
      if (selectedOrderLines.filter(item => item.ID === changeOrderLineID).length > 0) {
        const otherOrderIDs = [];
        const noCorrespondingInsuranceOrderIDs = [];
        this.groupedUpdate = true;
        const changeInsuranceID = diffValues[changeFieldName].orderLineInsuranceParam
          ? Number(Object.keys(diffValues[changeFieldName].orderLineInsuranceParam)[0].replace('insuranceID-', ''))
          : Number(Object.keys(diffValues[changeFieldName]['Check Book Sheet'])[0].replace('insuranceID-', ''));
        const changeOrder = orders.find(order =>
          order['Order Line'].findIndex(orderLineItem =>
            orderLineItem.ID === changeOrderLineID) !== -1);
        const changePayerID = changeOrder.Insurance.find(item =>
          item.ID === changeInsuranceID).ID;
        selectedOrderLines.forEach((orderLine) => {
          const currentOrder = orders.find(order =>
            order['Order Line'].findIndex(orderLineItem =>
              orderLineItem.ID === orderLine.ID) !== -1);
          const currentChangeInsurance = currentOrder.Insurance.find(item =>
            item.ID === changePayerID);
          const isOtherOrder = currentOrder['Order Line'].filter(item => item.ID === changeOrderLineID).length === 0;
          if (isOtherOrder && otherOrderIDs.indexOf(orderLine['Order ID']) === -1) {
            otherOrderIDs.push(orderLine['Order ID']);
          }
          if (isOtherOrder
            && !currentChangeInsurance
            && noCorrespondingInsuranceOrderIDs.indexOf(orderLine['Order ID']) === -1) {
            noCorrespondingInsuranceOrderIDs.push(orderLine['Order ID']);
          }
          if (currentChangeInsurance) {
            Object.keys(diffValues[changeFieldName]).forEach((fieldName) => {
              if (typeof diffValues[changeFieldName][fieldName] === 'object') {
                Object.keys(diffValues[changeFieldName][fieldName]).forEach((nestedFieldName) => {
                  if (typeof diffValues[changeFieldName][fieldName][nestedFieldName] === 'object') {
                    Object.keys(diffValues[changeFieldName][fieldName][nestedFieldName])
                      .forEach((innerNestedFieldName) => {
                        actions.change(`${patient['Record Number']}-ordersForm`,
                          `${orderLine.ID}-orderLine.${fieldName}.insuranceID-${currentChangeInsurance.ID}.${innerNestedFieldName}`,
                          diffValues[changeFieldName][
                            fieldName
                          ][nestedFieldName][innerNestedFieldName]);
                      });
                  }
                });
              } else {
                actions.change(`${patient['Record Number']}-ordersForm`, `${orderLine.ID}-orderLine.${fieldName}`, diffValues[changeFieldName][fieldName]);
              }
            });
          }
        });
        // Delay to wait multiple async change actions to complete
        setTimeout(() => {
          if (otherOrderIDs.length > 0) {
            if (otherOrderIDs.length === noCorrespondingInsuranceOrderIDs.length) {
              this.setState({
                syncUpdatesSnackbarOpen: true,
                syncUpdatesSnackbarText: 'Selected Order Lines do not have same Insurance, so update will not be synchronized',
                syncUpdatesSnackbarButtons: false,
              });
              actions.submit(`${patient['Record Number']}-ordersForm`);
            } else if (noCorrespondingInsuranceOrderIDs.length === 0) {
              this.setState({
                syncUpdatesSnackbarOpen: true,
                syncUpdatesSnackbarText: 'You are trying to update order lines from different orders, proceed?',
                syncUpdatesSnackbarButtons: true,
              });
              window.addEventListener('keydown', this.onWindowKeyDowm);
            } else {
              this.setState({
                syncUpdatesSnackbarOpen: true,
                syncUpdatesSnackbarText: `You are trying to update order lines from different orders, proceed?
                  Please note, selected Order Lines from
                  ${formatSinglePlural({ quantity: noCorrespondingInsuranceOrderIDs.length, single: 'Order', plural: 'Orders' })}
                  ${noCorrespondingInsuranceOrderIDs.map(item => formatOrderNumber(item)).join(', ')}
                  do not have same Insurance, so update will not be synchronized`,
                syncUpdatesSnackbarButtons: true,
              });
              window.addEventListener('keydown', this.onWindowKeyDowm);
            }
          } else {
            actions.submit(`${patient['Record Number']}-ordersForm`);
          }
          this.groupedUpdate = false;
        }, 200);
      } else {
        setTimeout(() => {
          actions.submit(`${patient['Record Number']}-ordersForm`);
        }, 100);
      }
    } else {
      const changeInsuranceKey = diffValues[changeFieldName].orderLineInsuranceParam
        ? Object.keys(diffValues[changeFieldName].orderLineInsuranceParam)[0]
        : null;
      const changeOrder = orders.find(order =>
        order['Order Line'].findIndex(orderLine =>
          orderLine.ID === changeOrderLineID) !== -1);
      actions.submit(`${patient['Record Number']}-ordersForm`);
      if (changeOrder['Order Line'].length > 1
        && (!changeInsuranceKey || !diffValues[changeFieldName].orderLineInsuranceParam[changeInsuranceKey].Reimbs)) {
        this.setState({
          groupedUpdateValues: diffValues[changeFieldName],
          groupedUpdateOrderID: changeOrder.ID,
        });
      }
    }
  }

  onGroupedUpdateConfirm() {
    const { actions, patient, orders } = this.props;
    const { groupedUpdateValues, groupedUpdateOrderID } = this.state;
    const groupedUpdateOrder = orders.find(order => order.ID === groupedUpdateOrderID);
    this.groupedUpdate = true;
    this.setState({
      groupedUpdateValues: null,
      groupedUpdateSnackbarOpen: false,
    });
    window.removeEventListener('keydown', this.onWindowKeyDowm);
    groupedUpdateOrder['Order Line'].forEach((orderLine) => {
      Object.keys(groupedUpdateValues).forEach((fieldName) => {
        if (typeof groupedUpdateValues[fieldName] === 'object') {
          Object.keys(groupedUpdateValues[fieldName]).forEach((nestedFieldName) => {
            if (typeof groupedUpdateValues[fieldName][nestedFieldName] === 'object') {
              Object.keys(groupedUpdateValues[fieldName][nestedFieldName])
                .forEach((innerNestedFieldName) => {
                  actions.change(`${patient['Record Number']}-ordersForm`,
                    `${orderLine.ID}-orderLine.${fieldName}.${nestedFieldName}.${innerNestedFieldName}`,
                    groupedUpdateValues[fieldName][nestedFieldName][innerNestedFieldName]);
                });
            } else {
              actions.change(`${patient['Record Number']}-ordersForm`, `${orderLine.ID}-orderLine.${fieldName}.${nestedFieldName}`, groupedUpdateValues[fieldName][nestedFieldName]);
            }
          });
        } else {
          actions.change(`${patient['Record Number']}-ordersForm`, `${orderLine.ID}-orderLine.${fieldName}`, groupedUpdateValues[fieldName]);
        }
      });
    });
    // Delay to wait multiple async change actions to complete
    setTimeout(() => {
      actions.submit(`${patient['Record Number']}-ordersForm`);
      this.groupedUpdate = false;
    }, 200);
  }

  onGroupedUpdateSnackbarClose() {
    const { orderLines, actions } = this.props;
    const { groupedUpdateEmptyDriverIDs, groupedUpdateEmptyDateOfDeliveryIDs } = this.state;
    const newState = {
      groupedUpdateValues: null,
      groupedUpdateSnackbarOpen: false,
    };
    if ((groupedUpdateEmptyDriverIDs || groupedUpdateEmptyDateOfDeliveryIDs) && !this.groupedUpdate) {
      const notificationMessage = [
        groupedUpdateEmptyDriverIDs.length > 0
          ? `${formatSinglePlural({
            quantity: groupedUpdateEmptyDriverIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${orderLines
              .filter(item => groupedUpdateEmptyDriverIDs.indexOf(item.ID) !== -1)
              .map(item => item['HCPC S Code']['HCPC S Code'])
              .join(', ')} ${formatSinglePlural({
            quantity: groupedUpdateEmptyDriverIDs.length,
            single: 'has',
            plural: 'have',
          })} missing Driver information`
          : null,
        groupedUpdateEmptyDateOfDeliveryIDs.length > 0
          ? `${formatSinglePlural({
            quantity: groupedUpdateEmptyDateOfDeliveryIDs.length,
            single: 'Code',
            plural: 'Codes',
          })} ${orderLines
              .filter(item => groupedUpdateEmptyDateOfDeliveryIDs.indexOf(item.ID) !== -1)
              .map(item => item['HCPC S Code']['HCPC S Code'])
              .join(', ')} ${formatSinglePlural({
            quantity: groupedUpdateEmptyDateOfDeliveryIDs.length,
            single: 'has',
            plural: 'have',
          })} missing Date of Delivery information`
          : null,
      ].filter(item => item).join(', ');
      actions.addNotification({
        message: notificationMessage,
        variant: 'info',
      });
      newState.groupedUpdateEmptyDriverIDs = null;
      newState.groupedUpdateEmptyDateOfDeliveryIDs = null;
    }
    this.setState(newState);
    window.removeEventListener('keydown', this.onWindowKeyDowm);
  }

  onSyncUpdatesConfirm() {
    const { patient, actions } = this.props;
    actions.submit(`${patient['Record Number']}-ordersForm`);
    this.setState({ syncUpdatesSnackbarOpen: false });
    window.removeEventListener('keydown', this.onWindowKeyDowm);
  }

  onSyncUpdatesSnackbarClose() {
    this.setState({ syncUpdatesSnackbarOpen: false });
    window.removeEventListener('keydown', this.onWindowKeyDowm);
  }

  onOrdersActionsBarEntered() {
    this.setState({ ordersActionsBarEntered: true });
  }

  onOrdersActionsBarExited() {
    this.setState({ ordersActionsBarEntered: false });
  }

  onEditPatientInformationButtonClick() {
    const { patient, history } = this.props;
    const { activeSection } = this.state;
    if (activeSection === 'profile') {
      this.setState({ editPatientInformation: !this.state.editPatientInformation });
    } else {
      history.replace(`/charts/${patient['Record Number']}/profile#patient-information`);
    }
  }

  onEditPatientInformationCancel() {
    this.setState({ editPatientInformation: false });
  }

  onWindowMessage(event) {
    const { actions } = this.props;
    if (event.data.type && event.data.type === 'order-update-success' && event.data.values && event.data.values.data) {
      Object.keys(event.data.values.data).forEach((orderID) => {
        actions.updateOrderSuccess(event.data.values.data[orderID]);
      });
      actions.addNotification({
        message: 'Order successfully updated',
        variant: 'success',
      });
    }
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.orders.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  onToolbarResize({ expandedView, expansionDetailsHeight, mobileFilterHeight }) {
    this.setState({
      toolbarExpanded: expandedView,
      toolbarHeight: expansionDetailsHeight,
      mobileFilterHeight: mobileFilterHeight,
    });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  render() {
    const {
      patient,
      tabs,
      location,
      orders,
      archiveOrders,
      archive,
      loading,
      selectedOrderLines,
      selectedOrders,
      orderLines,
      actions,
      sort,
      contextFiltersOptions,
      archiveContextFiltersOptions,
      count,
      archiveOrdersCount,
      business,
      documentsFilters,
      classes,
      width,
      ordersFilters,
      theme,
    } = this.props;
    const {
      activeSection,
      printDialogOpen,
      signDialogOpen,
      printFormValues,
      printDialogOrder,
      groupedUpdateSnackbarOpen,
      syncUpdatesSnackbarOpen,
      ordersActionsBarEntered,
      limit,
      offset,
      editPatientInformation,
      toolbarExpanded,
      toolbarHeight,
      mobileFilterExpanded,
      mobileFilterHeight,
    } = this.state;
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    const ordersActionsBarOpen = selectedOrderLines.length > 0;
    let combinedOrders = [
      ...orders,
      ...archive ? archiveOrders : [],
    ];
    let combinedContextFiltersOptions = contextFiltersOptions;
    if (archive) {
      if (combinedContextFiltersOptions && archiveContextFiltersOptions) {
        combinedContextFiltersOptions = {
          ...combinedContextFiltersOptions,
          cities: uniqBy([
            ...combinedContextFiltersOptions.cities,
            ...archiveContextFiltersOptions.cities,
          ], 'name'),
          drivers: uniqBy([
            ...combinedContextFiltersOptions.drivers,
            ...archiveContextFiltersOptions.drivers,
          ], 'ID'),
          hcpcsCodes: uniqBy([
            ...combinedContextFiltersOptions.hcpcsCodes,
            ...archiveContextFiltersOptions.hcpcsCodes,
          ], 'ID'),
          insurances: uniqBy([
            ...combinedContextFiltersOptions.insurances,
            ...archiveContextFiltersOptions.insurances,
          ], 'ID'),
          orderLineTypes: uniqBy([
            ...combinedContextFiltersOptions.orderLineTypes,
            ...archiveContextFiltersOptions.orderLineTypes,
          ], 'name'),
          sales: uniqBy([
            ...combinedContextFiltersOptions.sales,
            ...archiveContextFiltersOptions.sales,
          ], 'ID'),
          statuses: uniqBy([
            ...combinedContextFiltersOptions.statuses,
            ...archiveContextFiltersOptions.statuses,
          ], 'ID'),
          users: uniqBy([
            ...combinedContextFiltersOptions.users,
            ...archiveContextFiltersOptions.users,
          ], 'ID'),
          watchers: uniqBy([
            ...combinedContextFiltersOptions.watchers,
            ...archiveContextFiltersOptions.watchers,
          ], 'ID'),
        };
      }
      switch (`${this.props.sortBy} ${sort}`) {
      case 'Date of Creation desc':
        combinedOrders = orderBy(combinedOrders, [(item) => moment(item['Date of Creation'], dateFormat).format('X')], ['desc']);
        break;
      case 'Date of Creation asc':
        combinedOrders = orderBy(combinedOrders, [(item) => moment(item['Date of Creation'], dateFormat).format('X')], ['asc']);
        break;
      case 'Date of Delivery desc':
        combinedOrders = orderBy(combinedOrders, [(item) => moment(item['Order Line'][0]['Date of Delivery'], timeDateFormat).format('X')], ['desc']);
        break;
      case 'Date of Delivery asc':
        combinedOrders = orderBy(combinedOrders, [(item) => moment(item['Order Line'][0]['Date of Delivery'], timeDateFormat).format('X')], ['asc']);
        break;
      case 'Date of Service desc':
        combinedOrders = orderBy(combinedOrders, [(item) => moment(item['Order Line'][0]['Date of Service From'], dateFormat).valueOf()], ['desc']);
        break;
      case 'Date of Service asc':
        combinedOrders = orderBy(combinedOrders, [(item) => moment(item['Order Line'][0]['Date of Service From'], dateFormat).valueOf()], ['asc']);
        break;
      case 'Sales Representative Name desc':
        combinedOrders = orderBy(combinedOrders, [(item) =>
          item['Sales Representative'][0]
          ? `${item['Sales Representative'][0]['First Name']} ${item['Sales Representative'][0]['Last Name']}`
          : 0], 'desc');
        break;
      case 'Sales Representative Name asc':
        combinedOrders = orderBy(combinedOrders, [(item) =>
          item['Sales Representative'][0]
          ? `${item['Sales Representative'][0]['First Name']} ${item['Sales Representative'][0]['Last Name']}`
          : 0], 'asc');
        break;
      case 'Insurance Name desc':
        combinedOrders = orderBy(combinedOrders, [(item) =>
          item['Primary Insurance'].ID
          ? item['Primary Insurance']['Payer Name']
          : 0], 'desc');
        break;
      case 'Insurance Name asc':
        combinedOrders = orderBy(combinedOrders, [(item) =>
          item['Primary Insurance'].ID
          ? item['Primary Insurance']['Payer Name']
          : 0], 'asc');
      case 'Prescriber Name desc':
        combinedOrders = orderBy(combinedOrders, [(item) =>
          item.Prescriber.ID
          ? formatPrescriberName(item.Prescriber)
          : 0], 'desc');
        break;
      case 'Prescriber Name asc':
        combinedOrders = orderBy(combinedOrders, [(item) =>
          item.Prescriber.ID
          ? formatPrescriberName(item.Prescriber)
          : 0], 'asc');
        break;
      }
    }

    let tableHeightAdjustment = toolbarExpanded ? toolbarHeight + 385 : 385;
    if (width === 'xs' || width === 'sm') {
      tableHeightAdjustment = mobileFilterExpanded ? mobileFilterHeight + 262 : 254;
    }
    if (ordersActionsBarOpen) {
      tableHeightAdjustment += 72;
    }
    return (
      <Layout>
        {(loading > 0 || !patient) &&
          <Fragment>
            <StickyToolbar
              className={classes.toolbar}
              disableGutters
            >
              {(tabs.findIndex(item => item.path === location.pathname) !== -1) &&
                <PatientsTabs
                  variant="patient"
                  loading={loading > 0 && !patient}
                />
              }
            </StickyToolbar>
            <Progress fixed />
          </Fragment>
        }
        {(patient
          && patient['First Name']
          && patient['Patient Global Notes']) &&
          <Fragment>
            <Helmet>
              <title>
                {appTitle} {titleSeparator} {patient['First Name']}, {patient['Last Name']}
              </title>
            </Helmet>
            <StickyToolbar
              className={classes.toolbar}
              disableGutters
            >
              {(tabs.findIndex(item => item.path === location.pathname) !== -1) &&
                <PatientsTabs
                  variant="patient"
                  loading={loading > 0 && !patient}
                />
              }
              <PatientDetailsBar
                patient={patient}
                activeSection={activeSection}
                onTabChange={this.onTabChange}
                onEditPatientInformationButtonClick={this.onEditPatientInformationButtonClick}
                business={business}
              />
              {activeSection === 'summary' &&
                <Fragment>
                  {/* {combinedOrders.length > 0 && */}
                    <div className={classes.mobileFilterHeader}>
                      <IconButton
                        onClick={this.onMobileFilterBottonClick}
                        className={classNames(classes.buttonFilter, {
                          [classes.buttonFilterActive]: ordersFilters && Object.keys(ordersFilters).length > 0,
                        })}
                      >
                        <IconFilter className={classes.iconFilter} />
                      </IconButton>
                      
                      <Typography className={classes.typography}>
                        Orders ({archive ? count + archiveOrdersCount : count})
                      </Typography>
                    </div>
                  {/* } */}
                  <OrdersFilter
                    className={classes.filter}
                    variant="patient"
                    form={`${patient['Record Number']}-ordersFilter`}
                    destroyOnUnmount={false}
                    {...combinedContextFiltersOptions ? {
                      contextFiltersOptions: combinedContextFiltersOptions,
                    } : {}}
                    onExpansionChange={this.onToolbarResize}
                    mobileFilterExpanded={mobileFilterExpanded}
                    {...params['order-id'] ? {
                      initialValues: {
                        'Order ID': Number(params['order-id']),
                      },
                    } : {}}
                  />
                </Fragment>
              }
              {/* {(activeSection === 'summary' || activeSection === 'history') &&
                <OrdersTableNoBody
                  className={classes.ordersTableHead}
                  items={combinedOrders}
                  variant="combined"
                  sort={sort}
                  sortBy={this.props.sortBy}
                  onSortingChange={this.onSortingChange}
                  loading={loading}
                  count={archive ? count + archiveOrdersCount : count}
                  ordersTableHeadContainerRef={(ordersTableHeadContainerRef) => {
                    this.ordersTableHeadContainerRef = ordersTableHeadContainerRef;
                  }}
                  onMobileFilterBottonClick={this.onMobileFilterBottonClick}
                />
              } */}
            </StickyToolbar>
            {(activeSection === 'summary' || activeSection === 'history') &&
              <Fragment>
                <ExpansionPanel
                  className={classes.expansionPanel}
                  elevation={0}
                  expanded={width === 'xs' || width === 'sm' ? mobileFilterExpanded : toolbarExpanded}
                  CollapseProps={{
                    timeout: theme.transitions.duration.short,
                  }}
                >
                  <ExpansionPanelDetails
                    className={classes.expansionPanelDetails}
                  >
                    <div
                      style={{
                        height: width === 'xs' || width === 'sm' ? mobileFilterHeight + 8 : toolbarHeight,
                      }}
                    />
                  </ExpansionPanelDetails>
                </ExpansionPanel>
                {/* <div
                  style={{
                    height: (width === 'xs' || width === 'sm') ? 230 : 343,
                  }}
                /> */}
                <div
                  className={classes.ordersContainer}
                  // style={{
                  //   paddingTop: combinedOrders.length === 0 && loading === 0 ? 258 : 343,
                  // }}
                >
                  <OrdersTable
                    items={combinedOrders}
                    style={{
                      height: `calc(100vh - ${tableHeightAdjustment}px)`,
                      height: `calc(100dvh - ${tableHeightAdjustment}px)`,
                    }}
                    variant="combined"
                    sort={sort}
                    sortBy={this.props.sortBy}
                    onSortingChange={this.onSortingChange}
                    gutterBottom={ordersActionsBarOpen}
                    form={`${patient['Record Number']}-ordersForm`}
                    onChange={this.onFormChange}
                    onSubmitSuccess={this.onFormSubmitSuccess}
                    onFormTextFieldBlur={this.onFormTextFieldBlur}
                    onStockLineCreate={() => {
                      this.onOrdersRequest({
                        push: false,
                        offset: 0,
                        limit: offset + limit,
                      });
                    }}
                    loading={loading}
                    count={archive ? count + archiveOrdersCount : count}
                    business={business}
                    onScroll={this.onTableScroll}
                    scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
                    initialValues={combinedOrders.reduce((ordersAccumulator, currentOrder) => ({
                      ...ordersAccumulator,
                      ...{
                        [`${currentOrder.ID}-order-selected`]: selectedOrders.filter(order =>
                          order.ID === currentOrder.ID).length > 0,
                      },
                      ...currentOrder['Order Line']
                        .reduce((orderLinesAccumulator, currentOrderLineValue) => ({
                          ...orderLinesAccumulator,
                          [`${currentOrderLineValue.ID}-orderLine`]: {
                            selected: selectedOrderLines.findIndex(selectedOrderLine =>
                              selectedOrderLine.ID === currentOrderLineValue.ID) !== -1,
                            Status: currentOrderLineValue['Workflow Status'].map(item => ({
                              value: item.ID,
                              label: item.Name,
                            })),
                            ...currentOrderLineValue['Assigned User'].ID ? {
                              'Assigned User ID': currentOrderLineValue['Assigned User'].ID,
                              'Assigned User Name': `${currentOrderLineValue['Assigned User']['First Name']} ${currentOrderLineValue['Assigned User']['Last Name']}`,
                              'Selected Assigned User Name': `${currentOrderLineValue['Assigned User']['First Name']} ${currentOrderLineValue['Assigned User']['Last Name']}`,
                            } : {
                              'Assigned User ID': 'unassigned',
                            },
                            ...currentOrderLineValue.Driver.ID ? {
                              'Driver ID': currentOrderLineValue.Driver.ID,
                              'Driver Name': `${currentOrderLineValue.Driver['First Name']} ${currentOrderLineValue.Driver['Last Name']}`,
                              'Selected Driver Name': `${currentOrderLineValue.Driver['First Name']} ${currentOrderLineValue.Driver['Last Name']}`,
                            } : {},
                            ...currentOrderLineValue['Date of Delivery'] ? {
                              'Date of Delivery': moment(currentOrderLineValue['Date of Delivery'], timeDateFormat).format(dateFormat),
                              'Time of Delivery': moment(currentOrderLineValue['Date of Delivery'], timeDateFormat).format(timeFormat),
                            } : {},
                            ...currentOrderLineValue['Place of SRV'] ? {
                              'Place of SRV': currentOrderLineValue['Place of SRV'],
                            } : {},
                            ...currentOrderLineValue.Reimbs && currentOrderLineValue.Reimbs !== 'None' ? { Reimbs: formatPrice(currentOrderLineValue.Reimbs) } : {},
                            ...currentOrderLineValue['Reimbs Date'] ? { 'Reimbs Date': currentOrderLineValue['Reimbs Date'] } : {},
                            ...currentOrderLineValue['Check Book Sheet Link'] && currentOrderLineValue['Check Book Sheet Link'].length > 0
                              ? { 'Check Book Sheet': currentOrderLineValue['Check Book Sheet Link']
                                .reduce((accumulator, currentValue) => {
                                  if (currentValue['Check Book Sheet'].length === 0) {
                                    return accumulator;
                                  }
                                  return {
                                    ...accumulator,
                                    [`insuranceID-${currentValue['Insurance ID']}`]: {
                                      'Check Number':
                                        currentValue['Check Book Sheet'][currentValue['Check Book Sheet'].length - 1]['Check Number'],
                                      'Check Book Sheet ID':
                                        currentValue['Check Book Sheet'][currentValue['Check Book Sheet'].length - 1].ID,
                                    },
                                  };
                                }, {}),
                              } : {},
                            ...currentOrderLineValue['Order Line Insurance Param']
                              && currentOrderLineValue['Order Line Insurance Param'].length > 0 ? {
                                orderLineInsuranceParam: currentOrderLineValue['Order Line Insurance Param']
                                  .reduce((accumulator, currentValue) => ({
                                    ...accumulator,
                                    [`insuranceID-${currentValue.Insurance.ID}`]: {
                                      ...currentValue.ID ? {
                                        ID: currentValue.ID,
                                      } : {},
                                      ...currentValue.Reimbs ? {
                                        Reimbs: formatPrice(currentValue.Reimbs),
                                      } : {},
                                      ...currentValue['Reimbs Date'] ? {
                                        'Reimbs Date': currentValue['Reimbs Date'],
                                      } : {},
                                    },
                                  }), {}),
                              } : {},
                            orderID: currentOrder.ID,
                          },
                        }), {}),
                    }), {})}
                  />
                  {ordersActionsBarEntered &&
                    <div style={{ height: '72px' }} />
                  }
                  <OrdersActionsBar
                    open={ordersActionsBarOpen}
                    count={selectedOrderLines.length}
                    totalCount={orderLines.length}
                    selectedOrderLines={selectedOrderLines}
                    onPrint={this.onSelectedOrderLinesPrint}
                    onSelect={() => {
                      combinedOrders.forEach((order) => {
                        order['Order Line'].forEach((orderLine) => {
                          actions.change(
                            `${patient['Record Number']}-ordersForm`,
                            `${orderLine.ID}-orderLine.selected`,
                            true,
                          );
                        });
                        actions.change(`${patient['Record Number']}-ordersForm`, `${order.ID}-order-selected`, true);
                      });
                    }}
                    onDeselect={() => {
                      combinedOrders.forEach((order) => {
                        order['Order Line'].forEach((orderLine) => {
                          actions.change(
                            `${patient['Record Number']}-ordersForm`,
                            `${orderLine.ID}-orderLine.selected`,
                            false,
                          );
                        });
                        actions.change(`${patient['Record Number']}-ordersForm`, `${order.ID}-order-selected`, false);
                      });
                    }}
                    onSubmitSuccess={(values) => {
                      values.forEach((orderLine) => {
                        actions.change(
                          `${patient['Record Number']}-ordersForm`,
                          `${orderLine.ID}-orderLine.selected`,
                          false,
                        );
                      });
                      this.onOrdersRequest({
                        push: false,
                        offset: 0,
                        limit: offset + limit,
                      });
                    }}
                    variant="combined"
                    orders={combinedOrders}
                    onEntered={this.onOrdersActionsBarEntered}
                    onExited={this.onOrdersActionsBarExited}
                  />
                  {printDialogOrder &&
                    <Fragment>
                      <PrintDialog
                        form={`${printDialogOrder.ID}-orderPrint`}
                        variant="order"
                        open={printDialogOpen}
                        onClose={this.onPrintDialogClose}
                        onSign={this.onPrintDialogSign}
                        ID={printDialogOrder.ID}
                        patient={printDialogOrder.Patient}
                        prescriber={printDialogOrder.Prescriber}
                        {...{
                          'Order Line': printDialogOrder['Order Line'],
                          'Primary Insurance': printDialogOrder['Primary Insurance'],
                          'Order Insurance Param': printDialogOrder['Order Insurance Param'],
                        }}
                      />
                      <DocumentsSignDialog
                        open={signDialogOpen}
                        onClose={this.onSignDialogClose}
                        printFormValues={printFormValues}
                        variant="order"
                        ID={printDialogOrder.ID}
                        patient={printDialogOrder.Patient}
                        prescriber={printDialogOrder.Prescriber}
                        {...{
                          'Order Line': printDialogOrder['Order Line'],
                          'Primary Insurance': printDialogOrder['Primary Insurance'],
                          'Order Insurance Param': printDialogOrder['Order Insurance Param'],
                        }}
                      />
                    </Fragment>
                  }
                  <Snackbar
                    open={groupedUpdateSnackbarOpen}
                    variant="success"
                    message="Order was successfully updated. Apply this update to other order lines?"
                    autoHideDuration={3000}
                    resumeHideDuration={0}
                    onClose={this.onGroupedUpdateSnackbarClose}
                    action={(
                      <Button
                        variant="raised"
                        color="primary"
                        onClick={this.onGroupedUpdateConfirm}
                      >
                        Submit
                      </Button>
                    )}
                  />
                  <Snackbar
                    open={syncUpdatesSnackbarOpen}
                    variant="warning"
                    message="You are trying to update order lines from different orders, proceed?"
                    autoHideDuration={6000}
                    resumeHideDuration={0}
                    onClose={this.onSyncUpdatesSnackbarClose}
                    action={(
                      <Button
                        variant="raised"
                        color="primary"
                        onClick={this.onSyncUpdatesConfirm}
                      >
                        Submit
                      </Button>
                    )}
                  />
                </div>
              </Fragment>
            }
            {activeSection === 'profile' &&
              <div className={classes.patientProfileDetails}>
                <PatientProfile
                  patient={{
                    ...patient,
                    ...documentsFilters && documentsFilters['Documents Category ID']
                      && documentsFilters['Documents Category ID'].length > 0 ? {
                        Documents: patient.Documents.filter(item =>
                          item['Document Category'].filter(documentsItem =>
                            documentsFilters['Documents Category ID'].indexOf(documentsItem.ID) !== -1).length > 0),
                      } : {},
                  }}
                  hash={location.hash}
                  business={business}
                  onCancel={this.onEditPatientInformationCancel}
                  editPatientInformation={editPatientInformation}
                />
              </div>
            }
          </Fragment>
        }
      </Layout>
    );
  }
}

Patient.propTypes = {
  patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
    'First Name': PropTypes.string,
    'Last Name': PropTypes.string,
  }),
  orders: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  selectedOrders: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  connectedOrdersOrderLineID: PropTypes.arrayOf(PropTypes.number),
  orderLines: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  selectedOrderLines: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  actions: PropTypes.shape({
    fetchPatient: PropTypes.func.isRequired,
    fetchPatientGlobalNotes: PropTypes.func.isRequired,
    fetchOrders: PropTypes.func.isRequired,
    clearOrders: PropTypes.func.isRequired,
    clearConnectedOrders: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    addRouterParamsTab: PropTypes.func.isRequired,
    addRouterParamsPageSettings: PropTypes.func.isRequired,
    fetchBusiness: PropTypes.func.isRequired,
  }).isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
      activeSection: PropTypes.string,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    state: PropTypes.shape({
      activeSection: PropTypes.string,
    }),
    hash: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  ordersFilters: PropTypes.shape({
    'Order ID': PropTypes.string,
    'Claim Code': PropTypes.string,
    'Date of Service From': PropTypes.string,
    'Date of Service To': PropTypes.string,
    'Date of Creation': PropTypes.string,
    'Date of Creation From': PropTypes.string,
    'Date of Creation To': PropTypes.string,
    'Date of Claim Submission From': PropTypes.string,
    'Date of Claim Submission To': PropTypes.string,
  }).isRequired,
  notesFilters: PropTypes.shape({
    'Notes Category ID': PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  documentsFilters: PropTypes.shape({
    'Documents Category ID': PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  sortBy: PropTypes.string,
  sort: PropTypes.oneOf(['asc', 'desc']),
  userNotificationsInitialized: PropTypes.bool,
  contextFiltersOptions: PropTypes.shape({
    orderLineTypes: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      count: PropTypes.number,
    })).isRequired,
  }),
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  classes: PropTypes.shape({
    patientProfileDetails: PropTypes.string.isRequired,
    filter: PropTypes.string.isRequired,
    ordersTableHead: PropTypes.string.isRequired,
  }).isRequired,
};

Patient.defaultProps = {
  patient: null,
  connectedOrdersOrderLineID: [],
  sortBy: 'Date of Creation',
  sort: 'desc',
  contextFiltersOptions: null,
  business: {},
  userNotificationsInitialized: false,
};

const mapStateToProps = (store, ownProps) => {
  const recordNumber = Number(ownProps.match.params.id);
  const patient = store.patients.data.length > 0
    ? store.patients.data.find(patientsItem => patientsItem['Record Number'] === recordNumber)
    : null;
  const ordersFilterValues = getFormValues(`${recordNumber}-ordersFilter`)(store);
  const notesFilterValues = getFormValues(`${recordNumber}-notesFilter`)(store);
  const documentsFilterValues = getFormValues(`${recordNumber}-documentsFilter`)(store);
  const ordersFormValues = getFormValues(`${recordNumber}-ordersForm`)(store);
  const orderLines = store.orders.data.reduce((accumulator, currentValue) => ([
    ...accumulator,
    ...currentValue['Order Line'],
  ]), []);
  const selectedOrderLines = ordersFormValues
    ? Object.keys(ordersFormValues)
      .filter(orderLine => ordersFormValues[orderLine].selected === true)
      .map(orderLine => orderLines.find(item => item.ID === Number(orderLine.replace('-orderLine', ''))))
      .filter(item => item)
    : [];
  const selectedOrders = ordersFormValues && selectedOrderLines.length > 0
    ? Object.keys(ordersFormValues)
      .filter(filedName => filedName.indexOf('-order-selected') !== -1
        && ordersFormValues[filedName] === true)
      .map(filedName => store.orders.data.find(item => item.ID === Number(filedName.replace('-order-selected', ''))))
      .filter(item => item)
    : [];
  const State = [
    ...ordersFilterValues
      && ordersFilterValues.Intake ? [1] : [],
    ...ordersFilterValues
      && ordersFilterValues.Delivery ? [2] : [],
    ...ordersFilterValues
      && ordersFilterValues.Billing ? [3] : [],
    ...ordersFilterValues
      && ordersFilterValues.Collection ? [4] : [],
    ...ordersFilterValues
      && ordersFilterValues.Paid ? [5] : [],
  ];
  const archive = ordersFilterValues
    && ordersFilterValues.Archive
    ? ordersFilterValues.Archive
    : false;
  return {
    patient,
    orders: store.orders.data,
    count: store.orders.count,
    archiveOrders: store.archiveOrders.data,
    archiveOrdersCount: store.archiveOrders.count,
    loading: store.patients.loading
      + store.orders.loading + store.archiveOrders.loading,
    connectedOrdersOrderLineID: store.connectedOrders.orderLineID,
    tabs: store.routerParams.tabs.patient,
    orderLines,
    selectedOrderLines,
    selectedOrders,
    archive,
    userNotificationsInitialized: store.userNotifications.initialized,
    ordersFilters: {
      'Patient ID': ownProps.match.params.id,
      ...ordersFilterValues
        && ordersFilterValues['Order ID']
        ? { 'Order ID': ordersFilterValues['Order ID'] }
        : {},
      ...ordersFilterValues
        && ordersFilterValues['Claim Code']
        ? { 'Claim Code': ordersFilterValues['Claim Code'] }
        : {},
      ...State.length > 0 ? { 'Workflow State': State } : {},
      ...ordersFilterValues
        && ordersFilterValues.Rejected
        && State.length === 0
        ? {
          'Is Rejected': [true],
        } : {},
      ...ordersFilterValues
        && !ordersFilterValues.Rejected
        && State.length === 0
        ? {
          'Is Rejected': [false],
        } : {},
      ...ordersFilterValues
        && ordersFilterValues.Rejected
        && State.length > 0
        ? { 'Is Rejected': [true, false] } : {},
      ...ordersFilterValues
        && ordersFilterValues['Patient Name / Patient ID']
        ? { 'Patient Name / Patient ID': ordersFilterValues['Patient Name / Patient ID'] }
        : {},
      ...ordersFilterValues
        && ordersFilterValues['Patient Date of Birth']
        && !validate.date(ordersFilterValues['Patient Date of Birth'])
        ? { 'Patient Date of Birth': moment(ordersFilterValues['Patient Date of Birth'], dateFormat).format(dateFormat) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues['Patient Insurance Code']
        ? { 'Insurance Code': ordersFilterValues['Patient Insurance Code'] }
        : {},
      ...ordersFilterValues
        && ordersFilterValues['HCPC Code']
        && ordersFilterValues['HCPC Code'].length > 0
        ? { 'HCPC S Code ID': ordersFilterValues['HCPC Code'].map(item => item.value) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters.User
        && ordersFilterValues.Filters.User.length > 0
        && (!store.orders.contextFiltersOptions || archive
          || ordersFilterValues.Filters.User.length !== store.orders.contextFiltersOptions.users.length)
        ? { 'Assigned User ID': ordersFilterValues.Filters.User.map(item => item.value) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters.Status
        && ordersFilterValues.Filters.Status.length > 0
        && (!store.orders.contextFiltersOptions || archive
          || ordersFilterValues.Filters.Status.length !== store.orders.contextFiltersOptions.statuses.length)
        ? { Status: ordersFilterValues.Filters.Status.map(item => item.value) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters.Driver
        && ordersFilterValues.Filters.Driver.length > 0
        && (!store.orders.contextFiltersOptions || archive
          || ordersFilterValues.Filters.Driver.length !== store.orders.contextFiltersOptions.drivers.length)
        ? { 'Driver ID': ordersFilterValues.Filters.Driver.map(item => item.value) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters.Payer
        && ordersFilterValues.Filters.Payer.length > 0
        && (!store.orders.contextFiltersOptions || archive
          || ordersFilterValues.Filters.Payer.length !== store.orders.contextFiltersOptions.insurances.length)
        ? { 'Insurance Info ID': ordersFilterValues.Filters.Payer.map(item => item.value) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters.Type
        && ordersFilterValues.Filters.Type.length > 0
        && (!store.orders.contextFiltersOptions || archive
          || ordersFilterValues.Filters.Type.length !== store.orders.contextFiltersOptions.orderLineTypes.length)
        ? { 'Line Type': ordersFilterValues.Filters.Type.map(item => item.value) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters.Sales
        && ordersFilterValues.Filters.Sales.length > 0
        && (!store.orders.contextFiltersOptions || archive
          || ordersFilterValues.Filters.Sales.length !== store.orders.contextFiltersOptions.sales.length)
        ? { 'Sales Representative ID': ordersFilterValues.Filters.Sales.map(item => item.value) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters.Referral
        && ordersFilterValues.Filters.Referral.length > 0
        && (!store.orders.contextFiltersOptions || archive
          || ordersFilterValues.Filters.Referral.length !== store.orders.contextFiltersOptions.referralSources.length)
        ? { 'Referral Source ID': ordersFilterValues.Filters.Referral.map(item => item.value) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters.Prescriber
        && ordersFilterValues.Filters.Prescriber.length > 0
        && (!store.orders.contextFiltersOptions || archive
          || ordersFilterValues.Filters.Prescriber.length !== store.orders.contextFiltersOptions.prescribers.length)
        ? { 'Prescriber ID': ordersFilterValues.Filters.Prescriber.map(item => item.value) }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters['Date of Service']
        && ordersFilterValues.Filters['Date of Service'].length > 0
        ? {
          'Date of Service From': ordersFilterValues.Filters['Date of Service'][0].value.split('-')[0],
          'Date of Service To': ordersFilterValues.Filters['Date of Service'][0].value.split('-')[1],
        }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters['Date of Creation']
        && ordersFilterValues.Filters['Date of Creation'].length > 0
        ? {
          'Date of Creation From': ordersFilterValues.Filters['Date of Creation'][0].value.split('-')[0],
          'Date of Creation To': ordersFilterValues.Filters['Date of Creation'][0].value.split('-')[1],
        }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters['Date of Delivery']
        && ordersFilterValues.Filters['Date of Delivery'].length > 0
        ? {
          'Date of Delivery From': ordersFilterValues.Filters['Date of Delivery'][0].value.split('-')[0],
          'Date of Delivery To': ordersFilterValues.Filters['Date of Delivery'][0].value.split('-')[1],
        }
        : {},
      ...ordersFilterValues
        && ordersFilterValues.Filters
        && ordersFilterValues.Filters['Date of Claim Submission']
        && ordersFilterValues.Filters['Date of Claim Submission'].length > 0
        ? {
          'Date of Claim Submission From': ordersFilterValues.Filters['Date of Claim Submission'][0].value.split('-')[0],
          'Date of Claim Submission To': ordersFilterValues.Filters['Date of Claim Submission'][0].value.split('-')[1],
        }
        : {},
    },
    contextFiltersOptions: store.orders.contextFiltersOptions,
    archiveContextFiltersOptions: store.archiveOrders.contextFiltersOptions,
    ...store.routerParams.pageSettings[ownProps.location.pathname],
    business: store.business.data,
    notesFilters: {
      ...notesFilterValues
        && notesFilterValues['Notes Category']
        && notesFilterValues['Notes Category'].length > 0 ? {
          'Notes Category ID': notesFilterValues['Notes Category'].map(item =>
            item.value),
        } : {},
      ...notesFilterValues
        && notesFilterValues.Author
        && notesFilterValues.Author.length > 0 ? {
          'Author ID': notesFilterValues.Author.map(item =>
            item.value),
        } : {},
      ...notesFilterValues
        && notesFilterValues['Date of Creation From'] ? {
          'Date of Creation From': notesFilterValues['Date of Creation From'],
        } : {},
      ...notesFilterValues
        && notesFilterValues['Date of Creation To'] ? {
          'Date of Creation To': notesFilterValues['Date of Creation To'],
        } : {},
    },
    documentsFilters: {
      ...documentsFilterValues
        && documentsFilterValues['Documents Category']
        && documentsFilterValues['Documents Category'].length > 0 ? {
          'Documents Category ID': documentsFilterValues['Documents Category'].map(item =>
            item.value),
        } : {},
    },
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchPatient,
    fetchPatientGlobalNotes,
    fetchOrders,
    clearOrders,
    updateOrderSuccess,
    clearConnectedOrders,
    change,
    reset,
    submit,
    addNotification,
    addRouterParamsTab,
    addRouterParamsPageSettings,
    fetchBusiness,
    fetchArchiveOrders,
    clearArchiveOrders,
    initializeUserNotifications,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(withWidth()(withTheme()(Patient))),
);



// WEBPACK FOOTER //
// ./src/pages/charts/Patient.js